import React, {
  ReactElement,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import dotProp from 'dot-prop'
import classNames from 'classnames'
import Textarea from 'react-textarea-autosize'
import Select, {
  GroupedOptionsType,
  OptionsType,
  OptionTypeBase,
} from 'react-select'
import {
  FieldProps as FormikFieldProps,
  FormikErrors,
  FormikTouched,
} from 'formik'
import selectUtils from './reactSelectUtils'

const convertProps = (
  {
    field: { onChange, onBlur, value, name },
    form: { setFieldValue, setFieldTouched, touched, errors },
    afterChange,
    innerRef,
    ...args
  }: FormikFieldProps & {
    afterChange: (e: any) => {}
    innerRef: RefObject<HTMLElement>
  },
  select: boolean
) => {
  return {
    ...args,
    input: {
      onChange: async e => {
        if (select) {
          await setFieldValue(name, e)
          setFieldTouched(name)
        } else {
          onChange(e)
        }
        afterChange && afterChange(e)
      },
      onBlur: select ? () => setFieldTouched(name) : onBlur,
      onFocus: () => {},
      value,
      name,
      autoComplete: 'off',
      ref: innerRef,
    },
    meta: {
      touched: dotProp.get(touched, name),
      error: dotProp.get(errors, name),
      setFieldValue: val => setFieldValue(name, val),
    },
  }
}

type FieldWrapperProps = {
  label?: string
  horizontal?: boolean
  wrapperRef?: RefObject<HTMLDivElement>
  required?: boolean
  wrapperClassName?: string
  children: React.ReactNode
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  label,
  children,
  horizontal,
  wrapperRef,
  required,
  wrapperClassName,
}) => (
  <div
    className={classNames(`field ${wrapperClassName ? wrapperClassName : ''}`, {
      'is-horizontal': horizontal,
    })}
    ref={wrapperRef}
  >
    {horizontal ? (
      <div className="field-label is-normal">
        {label && (
          <label className="label">
            {label}
            {required && ' *'}
          </label>
        )}
      </div>
    ) : (
      label && (
        <label className="label">
          {label}
          {required && ' *'}
        </label>
      )
    )}
    <div className="field-body">{children}</div>
  </div>
)

export const Help = ({ settings }) => {
  let content: string
  let big = false

  if (typeof settings === 'object' && 'content' in settings) {
    content = settings.content
    big = settings.big
  } else {
    content = settings
  }

  if (!content) {
    return null
  }

  if (!big) {
    return <p className="help">{content}</p>
  }

  const [expandable, setExpandable] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current && ref.current.offsetHeight > 150) {
      setExpandable(true)
    }
  }, [])

  return (
    <article className="message">
      <div
        className={classNames('message-body content', {
          'message-expandable': expandable,
          'is-expanded': expanded,
        })}
        onClick={() => expandable && !expanded && setExpanded(true)}
        ref={ref}
      >
        {content}
      </div>
    </article>
  )
}

//

type FieldProps = {
  input: ReactElement
  meta: { touched: boolean; error: boolean }
  additional?: ReactElement
  help: string
  label?: string
  horizontal: boolean
  prefix?: ReactElement
  suffix?: ReactElement
  wrapperRef?: RefObject<HTMLDivElement>
  required?: boolean
  wrapperClassName?: string
}

export const Field: React.FC<FieldProps> = ({
  input,
  meta,
  additional,
  help,
  label,
  horizontal,
  prefix,
  suffix,
  wrapperRef,
  required,
  wrapperClassName,
}) => {
  const error = meta.touched && meta.error && (
    <p className="help is-danger">{meta.error}</p>
  )

  const bottom = (
    <>
      {error}
      {additional}
      <Help settings={help} />
    </>
  )

  return (
    <FieldWrapper
      label={label}
      required={required}
      horizontal={horizontal}
      wrapperRef={wrapperRef}
      wrapperClassName={wrapperClassName}
    >
      {!prefix && !suffix ? (
        <div className="field">
          <div className="control">{input}</div>

          {bottom}
        </div>
      ) : (
        <div className="field is-expanded">
          <div className="field has-addons">
            {prefix && (
              <p className="control">
                <a className="button is-static">{prefix}</a>
              </p>
            )}
            <p className="control is-expanded">{input}</p>
            {suffix && (
              <p className="control">
                <a className="button is-static">{suffix}</a>
              </p>
            )}
          </div>

          {bottom}
        </div>
      )}
    </FieldWrapper>
  )
}

type ErrorHelpProps = {
  touched: FormikTouched<any>
  errors: FormikErrors<any>
  name: string
}

export const ErrorHelp: React.FC<ErrorHelpProps> = ({
  touched,
  errors,
  name,
}) => {
  const fieldTouched = dotProp.get<boolean>(touched, name)
  const fieldError = dotProp.get<string>(errors, name)

  return fieldTouched && fieldError ? (
    <p className="help is-danger">{fieldError}</p>
  ) : null
}

//

export const TextFieldOrig = ({
  input,
  meta,
  label,
  additional,
  help,
  wrapperRef,
  horizontal = true,
  ...rest
}) => (
  <Field
    wrapperRef={wrapperRef}
    input={<input {...input} className="input" type="text" {...rest} />}
    meta={meta}
    additional={additional}
    help={help}
    label={label}
    horizontal={horizontal}
  />
)

export const TextField = p => (
  // @ts-ignore
  <TextFieldOrig {...convertProps(p)} />
)

//

export const TextareaAutosizeFieldOrig = ({
  input,
  meta,
  label,
  additional,
  help,
  minRows,
  maxRows,
  wrapperRef,
  horizontal = true,
  acceptNewLine = true,
  ...rest
}) => (
  <Field
    wrapperRef={wrapperRef}
    input={
      <Textarea
        {...input}
        onChange={e => {
          if (!acceptNewLine) {
            if (e.target.value.match(/\n/)) {
              e.target.value = e.target.value.replace(/\n/g, '')
            }
          }
          input.onChange(e)
        }}
        className="textarea"
        rows={minRows}
        minRows={minRows}
        maxRows={maxRows}
        {...rest}
      />
    }
    meta={meta}
    additional={additional}
    help={help}
    label={label}
    horizontal={horizontal}
  />
)

export const TextareaAutosizeField = p => (
  // @ts-ignore
  <TextareaAutosizeFieldOrig {...convertProps(p)} />
)

//

const SelectFieldOrig = ({
  input,
  meta,
  label,
  options,
  inModal,
  help,
  required,
  horizontal = true,
  wrapperClassName,
  isFormik = false,
  additional,
  ...rest
}) => (
  <Field
    input={
      <SelectFieldSingle
        input={input}
        options={options}
        inModal={inModal}
        isFormik={isFormik}
        {...rest}
      />
    }
    meta={meta}
    help={help}
    label={label}
    horizontal={horizontal}
    required={required}
    additional={additional}
    wrapperClassName={wrapperClassName}
  />
)

type SelectFieldSingleProps = {
  input: React.HTMLProps<HTMLInputElement>
  options: GroupedOptionsType<OptionTypeBase> | OptionsType<OptionTypeBase>
  inModal?: boolean
  small?: boolean
  isFormik?: boolean
}

const SelectFieldSingle: React.FC<SelectFieldSingleProps> = ({
  input,
  options,
  inModal,
  small,
  isFormik,
  ...rest
}) => {
  const value =
    options
      // @ts-ignore
      .flatMap(option => (option.options ? option.options : [option]))
      .find(({ value }) => value == input.value) || null

  return (
    // @ts-ignore
    <Select
      {...input}
      {...rest}
      onChange={selectUtils.onChangeFormikSingle(input)}
      onBlur={selectUtils.onBlurFormik(input)}
      value={value}
      options={options}
      styles={small ? selectUtils.stylesSmall : selectUtils.styles}
    ></Select>
  )
}

export const SelectField = p => (
  // @ts-ignore
  <SelectFieldOrig {...convertProps(p, true)} />
)

//

export const StaticField = ({ label, value, horizontal = true }) => (
  <FieldWrapper label={label} horizontal={horizontal}>
    <div className="field">
      <p className="control">
        <input className="input is-static" type="text" value={value} readOnly />
      </p>
    </div>
  </FieldWrapper>
)

export const StaticTextareaField = ({ label, value, horizontal = true }) => (
  <FieldWrapper label={label} horizontal={horizontal}>
    <div className="field">
      <p className="control">
        <textarea className="input is-static" value={value} readOnly />
      </p>
    </div>
  </FieldWrapper>
)
